'use client';

import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useCartStore } from '@/service/state/Cart/useCartStore';
import { ECartActionType } from '@/service/state/Cart/domain';
import { getFormattedPrice } from '@/service/utils/getFormattedPrice';
import { formattedDate } from '@/service/utils/formattedDate';
import { TCartItem } from '@/domain/Cart.domain';
import { FormCard } from '@/ui/common/Cards/FormCard';
import { FormButton } from '@/ui/common/Buttons/FormButton';
import { BrandLoader } from '@/ui/common/Loaders/BrandLoader';
import { ConfirmationPopup } from '@/ui/common/Popups/ConfirmationPopup';
import { CartEmpty } from '@/ui/components/CartContent/core/CartEmpty';
import { TicketExpirationInfo } from '../TicketExpirationInfo';
import { CartItem, PriceInfo, PromoCode } from './core';
import { CartExpired } from './core/CartExpired';
import { usePartnerStore } from '@/service/state/Partner/usePartnerStore';
import { trackUpadteCartCRS } from '@/service/CRS/crsEvents';
import { createCRSCartItems } from '@/service/CRS/crsCreators';

export const CartContent = () => {
    const { partner_slug } = useParams();
    const partnerSlug = partner_slug.toString();
    const partnerName = usePartnerStore((state) => state.siteurl);

    const [triggeredCartItemId, setTriggeredCartItemId] = useState('');

    const {
        cart,
        loading,
        getCart,
        delCart,
        removedCartItems,
        expiredCartItems,
        handleRemovedCartItemsChange,
        resetRemovedCartItems,
        setExpiredCartItems,
        getTips,
    } = useCartStore((state) => state);

    useEffect(() => {
        const pureSlug = partner_slug.toString();

        getCart({ partnerId: pureSlug }, ECartActionType.GET_CART);
    }, [getCart, getTips, partner_slug, resetRemovedCartItems]);

    useEffect(() => {
        if (partnerName && cart?.cart_items.length) {
            trackUpadteCartCRS(
                { cartItems: createCRSCartItems(cart.cart_items, partnerName) },
                partnerName
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerName, cart?.cart_items.length]);

    useEffect(() => {
        if (cart) setExpiredCartItems(cart.cart_items);
    }, [cart, setExpiredCartItems]);

    useEffect(
        () => () => {
            resetRemovedCartItems();
            setExpiredCartItems([]);
        },
        [resetRemovedCartItems, setExpiredCartItems]
    );

    const delFromCart = useCallback(
        (partnerSlug: string, cartItemId: string) => {
            handleRemovedCartItemsChange(cartItemId);
            delCart({ partnerId: partnerSlug, item_id: cartItemId });
            setTriggeredCartItemId('');
        },
        [handleRemovedCartItemsChange, delCart]
    );

    if (loading.state) {
        return <BrandLoader />;
    }

    if (!cart || !cart.cart_items.length) {
        return removedCartItems.length ? (
            <CartExpired partnerSlug={partnerSlug} cartItems={removedCartItems} />
        ) : expiredCartItems.length ? (
            <CartExpired partnerSlug={partnerSlug} cartItems={expiredCartItems} isExpired />
        ) : (
            <CartEmpty partnerSlug={partnerSlug} />
        );
    }

    return (
        <div className="wrapper flex min-h-screen flex-col items-center px-0 pb-12 pt-3 sm:pb-14 sm:pt-10">
            <TicketExpirationInfo partnerSlug={partnerSlug} />
            <div className="mb-5 flex flex-col items-center gap-2">
                <div className="flex items-center gap-2">
                    <span className="tx-heading-4 text-gray-2 dark:text-gray-3">Grand total:</span>
                    <span
                        className="tx-heading-2 text-gray-1 dark:text-white"
                        data-test-id="cart-price"
                    >
                        {getFormattedPrice(cart.grand_total)}
                    </span>
                </div>
                <span className="tx-large-medium text-gray-3 dark:text-dark-1">
                    An Exciting Adventure Awaits
                </span>
            </div>
            <FormCard className="flex flex-col gap-4 px-4 py-4 md:gap-[30px] lg:gap-10">
                <div className="mb-4 flex w-full flex-col-reverse items-center justify-center gap-[10px] sm:flex-row md:mb-0 md:gap-[30px] lg:gap-10">
                    <Link href={`/${partner_slug}/`} prefetch={false}>
                        <FormButton
                            type="button"
                            variant="outlined"
                            className="mx-0 w-[220px] px-6"
                            label="Continue Shopping"
                            data-test-id="continue-shopping-btn"
                        />
                    </Link>
                    <Link href={`/${partner_slug}/checkout/`} prefetch={false}>
                        <FormButton
                            type="button"
                            className="mx-0 h-10 w-[220px]"
                            label="Checkout now"
                            data-test-id="checkout-now-btn"
                        />
                    </Link>
                </div>
                <ul className="flex flex-col items-center gap-4 md:gap-7">
                    {removedCartItems.map((cri) => (
                        <li key={cri.id} className="tx-large-medium flex flex-col items-center">
                            <Link
                                href={`/${partner_slug}/details/${cri.product_id}`}
                                className="text-state-info underline"
                                prefetch={false}
                            >
                                {cri.product_name}
                            </Link>
                            <span className="text-gray-1 dark:text-white">
                                was removed from your cart
                            </span>
                        </li>
                    ))}
                </ul>
                <ul className="flex w-full flex-col gap-[14px]" data-test-id="cart-items">
                    {cart.cart_items.map((ci: TCartItem) => (
                        <CartItem
                            key={ci.id}
                            title={ci.product_name}
                            productId={ci.product_id}
                            bookedAt={
                                ci.arrival_date &&
                                formattedDate(ci.arrival_date, !!ci.schedule && ci.schedule.time)
                            }
                            returnAt={
                                ci.return_date &&
                                formattedDate(ci.return_date, !!ci.schedule && ci.schedule.time)
                            }
                            tickets={Object.values(ci.tickets)}
                            options={Object.values(ci.additional_options)}
                            comment={ci.comment_guest}
                            onRemoveCartItem={() => setTriggeredCartItemId(ci.id)}
                            subtotal={ci.subtotal}
                            activityLink={`/${partnerSlug}/details/${ci.product_id}`}
                            discountRate={ci.discount_rate}
                        />
                    ))}
                </ul>
                <PromoCode promoCode={cart.discount?.promo?.code} partnerSlug={partnerSlug} />
                <PriceInfo
                    subtotal={cart.subtotal}
                    surcharges={cart.surcharges}
                    total={cart.grand_total}
                    tips={cart.gratuity}
                />
                <div className="w-full px-5 md:px-10 lg:px-11">
                    <Link href={`/${partner_slug}/checkout/`} prefetch={false}>
                        <FormButton
                            type="button"
                            className="mt-[10px] max-w-full md:mt-1 lg:mt-2 lg:max-w-[250px]"
                            label="Checkout now"
                            data-test-id="cart-checkout-now-bottom-btn"
                        />
                    </Link>
                </div>
            </FormCard>
            <ConfirmationPopup
                className=""
                confirmText="Remove"
                cancelText="Cancel"
                questionText="Are you sure you want to remove?"
                infoText="You will lose your progress"
                isShown={!!triggeredCartItemId}
                onHide={() => setTriggeredCartItemId('')}
                onConfirm={() => delFromCart(partnerSlug, triggeredCartItemId)}
                onCancel={() => setTriggeredCartItemId('')}
            />
        </div>
    );
};
